import { useEffect, useState } from 'react';
import { get } from 'lodash';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControlLabel,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import Page from '../../components/Page';
import { getAvailableRoles, getAdminsForRoles, updateAdminRoles } from '../../axiosLib/services/adminRoles/admin';

export default function AdminRolesManager() {
  const [admins, setAdmins] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [editedRoles, setEditedRoles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setLoading(true);
    getAvailableRoles((err, result) => {
      if (!err) {
        setAvailableRoles(get(result, 'roles', []));
      }
    });
    getAdminsForRoles((err, result) => {
      setLoading(false);
      if (!err) {
        setAdmins(get(result, 'admins', []));
      } else {
        setSnackbar({ open: true, message: 'Failed to load admins', severity: 'error' });
      }
    });
  };

  const openEditDialog = (admin) => {
    setSelectedAdmin(admin);
    setEditedRoles([...(admin.roleSet || [])]);
    setDialogOpen(true);
  };

  const handleRoleToggle = (role) => {
    setEditedRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  };

  const handleSaveRoles = () => {
    if (!selectedAdmin) return;
    setSaving(true);

    updateAdminRoles(selectedAdmin._id, editedRoles, (err, result) => {
      setSaving(false);
      if (!err) {
        setSnackbar({ open: true, message: 'Roles updated!', severity: 'success' });
        setDialogOpen(false);
        // Update local state
        setAdmins((prev) =>
          prev.map((a) => (a._id === selectedAdmin._id ? { ...a, roleSet: editedRoles } : a))
        );
      } else {
        setSnackbar({ open: true, message: 'Failed to update roles', severity: 'error' });
      }
    });
  };

  return (
    <Page title="Admin Roles Manager">
      <Container maxWidth="lg">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4" gutterBottom>
            Admin Roles Manager
          </Typography>
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Card>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Username</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Roles</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {admins.map((admin, idx) => (
                    <TableRow key={admin._id} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        {get(admin, 'firstname', '') + ' ' + get(admin, 'lastname', '')}
                        {admin.nickName ? ` (${admin.nickName})` : ''}
                      </TableCell>
                      <TableCell>{get(admin, 'username', '')}</TableCell>
                      <TableCell>{get(admin, 'phone', '')}</TableCell>
                      <TableCell>
                        <Chip
                          label={admin.verified ? 'Active' : 'Inactive'}
                          size="small"
                          color={admin.verified ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          {(admin.roleSet || []).length > 0 ? (
                            admin.roleSet.map((role) => (
                              <Chip key={role} label={role} size="small" color="primary" variant="outlined" />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No roles
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => openEditDialog(admin)}
                          sx={{
                            background: 'linear-gradient(45deg, #00d4ff, #7b2ff7)',
                            '&:hover': { background: 'linear-gradient(45deg, #00b8d4, #6a1bd4)' },
                          }}
                        >
                          Edit Roles
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}

        {/* Edit Roles Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Edit Roles — {get(selectedAdmin, 'username', '')}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {get(selectedAdmin, 'firstname', '') + ' ' + get(selectedAdmin, 'lastname', '')}
              {selectedAdmin?.phone ? ` | ${selectedAdmin.phone}` : ''}
            </Typography>
            <Box>
              {availableRoles.map((role) => (
                <FormControlLabel
                  key={role}
                  control={
                    <Checkbox
                      checked={editedRoles.includes(role)}
                      onChange={() => handleRoleToggle(role)}
                      color="primary"
                    />
                  }
                  label={role}
                  sx={{ display: 'block', mb: 0.5 }}
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
              onClick={handleSaveRoles}
              disabled={saving}
              sx={{
                background: 'linear-gradient(45deg, #00d4ff, #7b2ff7)',
                '&:hover': { background: 'linear-gradient(45deg, #00b8d4, #6a1bd4)' },
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Page>
  );
}
